import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import {
  ArchiveExperienceRequestPathParamType,
  ArchiveExperienceResponseBodyType,
} from "@intelligems/ig-types/functions/experiences/archive-experience";
import { getOnsiteEditsFromExperienceDto } from "@src/api/formatters/from-dto/onsite-edits";
import { getOnsiteInjectionsFromExperienceDto } from "@src/api/formatters/from-dto/onsite-injections";
import { getUserInterfacesFromExperienceDto } from "@src/api/formatters/from-dto/user-interfaces";
import { offerKeys } from "@src/api/queryKeys";
import { ApiRequest } from "@src/api/types/api";
import { initAudiences } from "@src/store/slices/audiences";
import { initPersonalizationFromDto } from "@src/store/slices/experiences";
import { initOfferProducts } from "@src/store/slices/offerProducts";
import { initOffersFromVariations } from "@src/store/slices/offers";
import { initOnsiteEdits } from "@src/store/slices/onsiteEdits";
import { initOnsiteInjections } from "@src/store/slices/onsiteInjections";
import { initPageTargeting } from "@src/store/slices/pageTargeting";
import { initUserInterfaces } from "@src/store/slices/userInterfaces";
import { initVariations } from "@src/store/slices/variations";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { experienceKeys } from "../queryKeys";

const archiveExperience = async (
  payload: ApiRequest<undefined, ArchiveExperienceRequestPathParamType>
): Promise<ArchiveExperienceResponseBodyType> => {
  const resp = await api.patch<ArchiveExperienceResponseBodyType>(
    `/v2/experiences/${payload.pathParams.experienceId}/archive`,
    { experienceId: payload.pathParams.experienceId } // Explicitly pass the experienceId if needed
  );
  return resp.data;
};

export const useArchiveExperience = (repopulate?: boolean) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationKey: ["archiveExperience"],
    mutationFn: async (
      data: ApiRequest<undefined, ArchiveExperienceRequestPathParamType>
    ) => {
      return archiveExperience(data);
    },
    onSuccess: async (data, payload) => {
      if (payload.pathParams.experienceId) {
        queryClient.setQueryData(
          experienceKeys.one(payload.pathParams.experienceId),
          {
            experience: data.experience,
          }
        );
      }
      await queryClient.invalidateQueries({
        queryKey: experienceKeys.personalizationList,
      });
      await queryClient.invalidateQueries({
        queryKey: offerKeys.all,
      });

      if (repopulate) {
        dispatch(initPersonalizationFromDto(data.experience));

        dispatch(
          initOnsiteEdits(
            getOnsiteEditsFromExperienceDto(data.experience.variations)
          )
        );
        dispatch(initVariations({ variations: data.experience.variations }));
        dispatch(
          initOffersFromVariations({
            variations: data.experience.variations,
            isInitialize: true,
          })
        );
        dispatch(
          initOfferProducts({
            offerProducts: data.experience.offerProducts,
          })
        );
        dispatch(
          initPageTargeting({
            pageTargeting: data.experience.experiencePageTargeting,
          })
        );

        dispatch(initAudiences({ audience: data.experience.audience }));
        dispatch(
          initUserInterfaces(
            getUserInterfacesFromExperienceDto(data.experience)
          )
        );
        dispatch(
          initOnsiteInjections(
            getOnsiteInjectionsFromExperienceDto(data.experience.variations)
          )
        );
      }
      // Show a toast message confirming success
      toast.success("Experience Archived");
    },
    onError: () => {},
  });
};
